import { RadAIConfig } from '../config/radai-config';
import { getClarityAIFile } from './patient-visits-service';

function resolveVisitNoFromReportData(reportData) {
  if (!reportData || typeof reportData !== 'object') return undefined;
  var visit = reportData.visit || {};
  var patientInfo = reportData.patientInfo || {};
  var raw =
    reportData.visitNo != null
      ? reportData.visitNo
      : visit.visitNo != null
        ? visit.visitNo
        : patientInfo.visitNo != null
          ? patientInfo.visitNo
          : patientInfo.visit_no != null
            ? patientInfo.visit_no
            : reportData.accessionNumber;
  if (raw == null || raw === '') return undefined;
  var s = String(raw).trim();
  return s || undefined;
}

function parseAIMetadataFromFetchHeaders(headers) {
  if (!headers || typeof headers.get !== 'function') {
    return {
      requestedProvider: null,
      usedProvider: null,
      usedModel: null,
      fallbackApplied: false
    };
  }

  const requestedProvider = headers.get('X-AI-Provider-Requested');
  const usedProvider = headers.get('X-AI-Provider-Used');
  const usedModel = headers.get('X-AI-Model-Used');
  const fallbackHeader = headers.get('X-AI-Fallback-Applied');

  return {
    requestedProvider: requestedProvider || null,
    usedProvider: usedProvider || null,
    usedModel: usedModel || null,
    fallbackApplied: String(fallbackHeader).toLowerCase() === 'true'
  };
}

export const RadAIService = {
  // Get configuration
  getConfig: () => RadAIConfig,
  getClaritySettings: async () => {
    const config = RadAIService.getConfig();
    const response = await fetch(`${config.api.baseUrl}/settings`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include'
    });
    if (!response.ok) throw new Error(`Failed to load settings: ${response.status}`);
    return response.json();
  },

  updateClaritySettings: async (payload) => {
    const config = RadAIService.getConfig();
    const response = await fetch(`${config.api.baseUrl}/settings`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload)
    });
    if (!response.ok) throw new Error(`Failed to save settings: ${response.status}`);
    return response.json();
  },

  resolveVisitNoFromReportData,

  // Clean text chunks for better display
  cleanTextChunk: (text) => {
    if (!text) return text;
    
    // Fix common character encoding issues
    let cleaned = text
      // Fix apostrophes and quotes
      .replace(/\?s/g, "'s")
      .replace(/\?t/g, "'t")
      .replace(/\?re/g, "'re")
      .replace(/\?ll/g, "'ll")
      .replace(/\?ve/g, "'ve")
      .replace(/\?d/g, "'d")
      .replace(/\?m/g, "'m")
      // Fix other common encoding issues
      .replace(/â€™/g, "'")
      .replace(/â€œ/g, '"')
      .replace(/â€/g, '"')
      .replace(/â€"/g, '—')
      .replace(/â€"/g, '–')
      // Fix spacing around punctuation for medical reports
      .replace(/(\w):/g, '$1: ')  // Add space after colons
      .replace(/(\w)-(\w)/g, '$1- $2')  // Add space after hyphens in lists
      // Clean up multiple spaces but preserve intentional spacing
      .replace(/  +/g, ' ');
    
    return cleaned;
  },

  markdownToPlainText: (md) => {
    if (!md) return '';
    const out = [];
    for (let line of md.split('\n')) {
      // Table separator rows (only pipes/dashes/colons/spaces): drop.
      if (line.includes('-') && /^\s*\|?[\s:|-]+\|?\s*$/.test(line)) continue;
      // Table rows: strip outer pipes, trim cell padding, rejoin compactly.
      if (/^\s*\|.*\|\s*$/.test(line)) {
        const cells = line.trim().replace(/^\||\|$/g, '').split('|').map(c => c.trim());
        line = cells.join(' | ');
      }
      // Headings: keep the text, drop the hashes.
      line = line.replace(/^#{1,6}\s+/, '');
      out.push(line);
    }
    return out.join('\n')
      .replace(/<br\s*\/?>/gi, '; ')
      .replace(/\*\*([^*]+)\*\*/g, '$1')
      .replace(/__([^_]+)__/g, '$1')
      .replace(/^(\s*)\*\s+/gm, '$1- ')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  },

  // Post-process complete messages for better medical report formatting
  formatMedicalReport: (text) => {
    if (!text) return text;
    
    let formatted = text
      // Fix common medical report formatting
      .replace(/(\*\*[^*\n]+\*\*)\s*:/g, '$1:\n')  // Add newline after bold headers with colons
      // Only whole lines that ARE a divider. The old /---\s*/g version also
      // matched the dashes inside GFM table separator rows (|---|---|) and
      // shredded every markdown table into a pile of horizontal rules.
      .replace(/^[ \t]*-{3,}[ \t]*$/gm, '---')  // Normalise standalone dividers
      .replace(/\n\s*\n\s*\n/g, '\n\n')  // Clean up excessive newlines
      // Improve list formatting. NOTE: [^:\n] (not [^:]) is critical — a character
      // class without \n excluded matches across newlines, which used to wrap
      // multiple lines of output in one giant ** ... ** span (stray ** artifacts).
      .replace(/^(\s*)-\s*([A-Z][^:\n]*:)(?!\*)/gm, '$1- **$2**')  // Bold list item headers
      // Fix spacing in medical terms
      .replace(/(\d+)\s*mm/g, '$1mm')
      .replace(/(\d+)\s*cm/g, '$1cm')
      .replace(/(\d+)\s*x\s*(\d+)/g, '$1x$2');
    
    return formatted;
  },

  // Simulate streaming for buffered content (proxy fallback)
  simulateStreaming: (content, onChunk, delay = 25) => {
    // Smart chunking: split by words but keep reasonable chunk sizes
    const chunks = [];
    const words = content.split(/(\s+)/); // Keep whitespace
    
    // Group words into reasonable chunks (2-4 characters each)
    let currentChunk = '';
    for (const word of words) {
      if (currentChunk.length + word.length > 4 && currentChunk.length > 0) {
        chunks.push(currentChunk);
        currentChunk = word;
      } else {
        currentChunk += word;
      }
    }
    if (currentChunk) chunks.push(currentChunk);

    // Send chunks with adaptive timing
    let index = 0;
    const sendNextChunk = () => {
      if (index < chunks.length) {
        const chunk = chunks[index];
        onChunk(chunk);
        index++;
        
        // Adaptive delay: shorter for small chunks, longer for bigger ones
        const adaptiveDelay = Math.min(delay + (chunk.length * 2), 60);
        setTimeout(sendNextChunk, adaptiveDelay);
      }
    };

    // Start the simulated streaming
    sendNextChunk();
  },

  // Initialize chat session
  initializeChat: () => {
    const config = RadAIService.getConfig();
    return {
      messages: [{
        id: Date.now(),
        role: 'system',
        content: config.chat.systemPrompt,
        timestamp: new Date().toISOString()
      }],
      sessionId: Date.now().toString(),
      isActive: true,
      config: config.model
    };
  },

  // Initialize Claire chat session with her own system prompt
  initializeClaireChat: () => {
    const config = RadAIService.getConfig();
    return {
      messages: [{
        id: Date.now(),
        role: 'system',
        content: config.claire.systemPrompt,
        timestamp: new Date().toISOString()
      }],
      sessionId: Date.now().toString(),
      isActive: true,
      config: config.model
    };
  },

  // Add message to chat session
  addMessage: (chatSession, message, role = 'user') => {
    chatSession.messages.push({
      id: Date.now(),
      role,
      content: message,
      timestamp: new Date().toISOString()
    });
    return chatSession;
  },

  // Generate report template
  generateReportTemplate: (reportData = {}) => {
    const { clinicalHistory, studyType, findings } = reportData;
    const config = RadAIService.getConfig();
    const sections = config.templates.default.sections;
    
    return sections.map(section => {
      switch(section) {
        case 'CLINICAL HISTORY':
          return `${section}: ${reportData.paraphrasedClinicalHistory || clinicalHistory || 'Clinical History to be provided'}`;
        case 'CLINICAL TECHNIQUE':
        case 'TECHNIQUE':
          return `${section}: ${studyType || 'Please specify imaging technique'}`;
        case 'FINDINGS':
          return `${section}: ${findings || 'Please describe key findings'}`;
        case 'IMPRESSION':
          return `${section}: [To be completed by ClarityAI]`;
        default:
          return `${section}: [To be completed]`;
      }
    }).join('\n\n');
  },

  // Post-process generated report to insert clinical history without affecting analysis
  insertClinicalHistoryIntoReport: (generatedReport, paraphrasedClinicalHistory) => {
    if (!paraphrasedClinicalHistory || !paraphrasedClinicalHistory.trim()) {
      return generatedReport;
    }

    const clinicalHistoryContent = paraphrasedClinicalHistory.trim();
    let updatedReport = generatedReport;

    // Step 1: Remove any incorrectly placed clinical history that might have been generated by ChatGPT
    // Remove lines that start with "Clinical History:" without proper formatting
    updatedReport = updatedReport.replace(/^Clinical History:\s*(.+)$/gm, '');
    
    // Step 2: Handle the standard placeholder replacement
    if (updatedReport.includes('[Clinical History to be inserted]')) {
      updatedReport = updatedReport.replace(
        /\[Clinical History to be inserted\]/gi, 
        clinicalHistoryContent
      );
      return updatedReport;
    }
    
    // Step 3: Find and replace Clinical History section properly
    // Look for "## Clinical History:" or "Clinical History:" followed by placeholder or empty content
    const clinicalHistoryPattern = /(#{1,2}\s*Clinical History:?\s*)(.*?)(?=\n#{1,2}\s|\n\n|\n$|$)/gims;
    const hasMatch = clinicalHistoryPattern.test(updatedReport);
    
    if (hasMatch) {
      updatedReport = updatedReport.replace(clinicalHistoryPattern, `$1${clinicalHistoryContent}\n\n`);
      return updatedReport;
    }
    
    // Step 4: If no Clinical History section found, add it at the beginning with proper formatting
    updatedReport = `## Clinical History:\n${clinicalHistoryContent}\n\n${updatedReport}`;
    
    return updatedReport;
  },

  // Insert default clinical history message when no clinical history is provided
  insertDefaultClinicalHistoryMessage: (generatedReport) => {
    const defaultMessage = 'Clinical History to be provided';
    let updatedReport = generatedReport;
    
    // Step 1: Remove any incorrectly placed clinical history
    updatedReport = updatedReport.replace(/^Clinical History:\s*(.+)$/gm, '');
    
    // Step 2: Handle the standard placeholder replacement
    if (updatedReport.includes('[Clinical History to be inserted]')) {
      updatedReport = updatedReport.replace(
        /\[Clinical History to be inserted\]/gi, 
        defaultMessage
      );
      return updatedReport;
    }
    
    // Step 3: Find and replace Clinical History section properly
    const clinicalHistoryPattern = /(#{1,2}\s*Clinical History:?\s*)(.*?)(?=\n#{1,2}\s|\n\n|\n$|$)/gims;
    const hasMatch = clinicalHistoryPattern.test(updatedReport);
    
    if (hasMatch) {
      updatedReport = updatedReport.replace(clinicalHistoryPattern, `$1${defaultMessage}\n\n`);
      return updatedReport;
    }
    
    // Step 4: If no Clinical History section found, add it at the beginning
    updatedReport = `## Clinical History:\n${defaultMessage}\n\n${updatedReport}`;
    
    return updatedReport;
  },

  // Paraphrase clinical history concisely
  paraphraseClinicalHistory: async (clinicalHistory, aiProvider) => {
    const config = RadAIService.getConfig();
    

    if (!clinicalHistory || !clinicalHistory.trim()) {
      return '';
    }

    const paraphrasePrompt = `Please paraphrase the following clinical history concisely and professionally for a radiology report. Keep it brief but comprehensive, maintaining all important medical information:

${clinicalHistory.trim()}

Please provide only the paraphrased clinical history without any additional commentary.`;

    const messages = [
      {
        role: 'system',
        content: 'You are a medical writing assistant specializing in concise, professional clinical documentation for radiology reports.'
      },
      {
        role: 'user',
        content: paraphrasePrompt
      }
    ];

    // Use backend endpoint only
    const response = await fetch(`${config.api.baseUrl}/radai/paraphrase-history`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include', // Include session cookies
      body: JSON.stringify({
        ...(aiProvider ? { aiProvider } : {}),
        clinicalHistory: clinicalHistory
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Backend API error: ${response.statusText}`);
    }

    const result = await response.json();
    return result.paraphrasedText;
  },

  // Generate report prompt using OCR data
  _generateReportPrompt: (ocrData, reportData = {}) => {
    const config = RadAIService.getConfig();
    const promptConfig = config.prompts.reportGeneration;
    
    // Format the instruction with visit description
    const visitDescription = reportData.findings || reportData.studyType || 'the imaging study';
    const formattedInstruction = promptConfig.instruction.replace('{visitDescription}', visitDescription);
    
    // Generate section instructions with special handling for Conclusions
    const sectionInstructions = promptConfig.reportStructure.map(section => {
      if (section.placeholder) {
        return `## ${section.name}:\n${section.placeholder}`;
      }
      return `## ${section.name}:\n${section.description}`;
    }).join('\n\n');

    // Include user input if provided (excluding clinical history to avoid bias)
    let userInputSection = '';
    
    // Add additional findings if provided
    if (reportData.additionalFindings && reportData.additionalFindings.trim()) {
      userInputSection += `

*Additional User Input/Findings:*
${reportData.additionalFindings.trim()}

Please consider the above user input when generating the report. Use this information to enhance your analysis and findings.`;
    }

    return `${formattedInstruction}

The report should be structured in the following way:
${sectionInstructions}

Important formatting notes:
- Do not use separators (--) between sections
- For the Conclusions section, only include the placeholder text: ${promptConfig.formatting.conclusionPlaceholder}
- For the Clinical History section, use the placeholder "[Clinical History to be inserted]"
- Focus your analysis solely on the imaging data provided, without any clinical bias
- Keep sections clean without extra formatting${userInputSection}

*Input of the text file*
${ocrData}`;
  },

  // Generate structured radiology report using ChatGPT with OCR data
  generateRadiologyReport: async (reportData, onProgress = null) => {
    const config = RadAIService.getConfig();
    

    // Step 1: Fetch ClarityAI file data if accession number is available
    let ocrData = null;
    let ocrError = null;
    
    if (reportData.accessionNumber) {
      try {
        const clarityResult = await getClarityAIFile(`${reportData.accessionNumber}.txt`);
        
        if (clarityResult.success && clarityResult.visit) {
          ocrData = clarityResult.visit;
        } else {
          ocrError = clarityResult.error || 'File not found';
          
          if (ocrError.includes('404') || ocrError.includes('not found')) {
            throw new Error('IMAGES_NOT_FOUND');
          }
        }
      } catch (error) {
        if (error.message === 'IMAGES_NOT_FOUND') {
          throw error;
        }
        
        ocrError = error.message;
      }
    }

    // Step 2: Ensure we have OCR data before proceeding
    if (!ocrData) {
      throw new Error('IMAGES_NOT_FOUND');
    }

    // Step 3: Generate prompt using OCR data
    const reportPrompt = RadAIService._generateReportPrompt(ocrData, reportData);
    const promptConfig = config.prompts.reportGeneration;
    
    // Step 4: Generate report using ChatGPT
    
    const messages = [
      {
        role: 'system',
        content: promptConfig.systemMessage
      },
      {
        role: 'user',
        content: reportPrompt
      }
    ];

    const visitNo = resolveVisitNoFromReportData(reportData);

    // Use backend endpoint only
    const requestBody = {
      ...(reportData && reportData.aiProvider ? { aiProvider: reportData.aiProvider } : {}),
      ...(visitNo ? { visitNo } : {}),
      reportData: {
        findings: ocrData || '',
        clinicalHistory: reportData.clinicalHistory || '',
        additionalFindings: reportData.additionalFindings || '',
        studyType: reportData.studyType || '',
        patientInfo: reportData.patientInfo || {},
        lungScreeningData: reportData.lungScreeningData || null,
        accessionNumber: reportData.accessionNumber,
        ...(reportData.templateContent && { templateContent: reportData.templateContent })
      }
      // No `options` block: temperature, token budget, and history handling are
      // backend-owned (driven by the user's saved settings), not client-sent.
    };

    const response = await fetch(`${config.api.baseUrl}/radai/generate-report`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include', // Include session cookies
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Backend API error: ${response.statusText}`);
    }

    const result = await response.json();
    const aiMetadata = parseAIMetadataFromFetchHeaders(response.headers);
    return {
      generatedReport: result.generatedReport,
      aiMetadata
    };
  },

  generateReportWithTemplate: async (reportData, templateId, templateType, onProgress = null) => {
    const config = RadAIService.getConfig();
    
    let ocrData = null;
    let ocrError = null;
    
    if (reportData.accessionNumber) {
      try {
        const clarityResult = await getClarityAIFile(`${reportData.accessionNumber}.txt`);
        
        if (clarityResult.success && clarityResult.visit) {
          ocrData = clarityResult.visit;
        } else {
          ocrError = clarityResult.error || 'File not found';
          
          if (ocrError.includes('404') || ocrError.includes('not found')) {
            throw new Error('IMAGES_NOT_FOUND');
          }
        }
      } catch (error) {
        if (error.message === 'IMAGES_NOT_FOUND') {
          throw error;
        }
        
        ocrError = error.message;
      }
    }

    if (!ocrData) {
      throw new Error('IMAGES_NOT_FOUND');
    }
    
    const promptConfig = config.prompts.reportGeneration;

    const visitNo = resolveVisitNoFromReportData(reportData);

    const requestBody = {
      ...(reportData && reportData.aiProvider ? { aiProvider: reportData.aiProvider } : {}),
      ...(visitNo ? { visitNo } : {}),
      reportData: {
        findings: ocrData || '',
        clinicalHistory: reportData.clinicalHistory || '',
        additionalFindings: reportData.additionalFindings || '',
        studyType: reportData.studyType || '',
        patientInfo: reportData.patientInfo || {}
      },
      templateId: templateId,
      templateType: templateType.toUpperCase()
    };

    const response = await fetch(`${config.api.baseUrl}/radai/generate-report-with-template`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Backend API error: ${response.statusText}`);
    }

    const result = await response.json();
    const aiMetadata = parseAIMetadataFromFetchHeaders(response.headers);
    return {
      generatedReport: result.generatedReport,
      aiMetadata
    };
  },


  // Streaming API call for Claire Chat (Backend Only)
  callClaireStream: async (messages, onChunk, onComplete, onError, onStatus = null) => {
    const config = RadAIService.getConfig();
    return RadAIService.callBackendStreamWithFallback(`${config.api.baseUrl}/claire/chat/stream`, messages, onChunk, onComplete, onError, null, onStatus);
  },

  // Streaming API call for RadAI chat (Backend Only)
  callRadAIStream: async (messages, onChunk, onComplete, onError, reportContext = null, onStatus = null) => {
    const config = RadAIService.getConfig();
    return RadAIService.callBackendStreamWithFallback(`${config.api.baseUrl}/radai/chat/stream`, messages, onChunk, onComplete, onError, reportContext, onStatus);
  },

  // Enhanced streaming with fallback for proxy environments
  callBackendStreamWithFallback: async (endpoint, messages, onChunk, onComplete, onError, reportContext = null, onStatus = null) => {
    let streamingWorked = false;
    let fallbackTimeout = null;
    
    // Set up fallback detection
    const startTime = Date.now();
    fallbackTimeout = setTimeout(() => {
      if (!streamingWorked) {
        console.info('[SSE] Proxy buffering detected - switching to optimized display mode for better UX');
      }
    }, 1500); // If no chunks in 1.5 seconds, likely buffered

    const wrappedOnChunk = (chunk) => {
      streamingWorked = true;
      if (fallbackTimeout) {
        clearTimeout(fallbackTimeout);
        fallbackTimeout = null;
      }
      onChunk(chunk);
    };

    const wrappedOnComplete = () => {
      if (fallbackTimeout) {
        clearTimeout(fallbackTimeout);
      }
      onComplete();
    };

    const wrappedOnError = (error) => {
      if (fallbackTimeout) {
        clearTimeout(fallbackTimeout);
      }
      onError(error);
    };

    return RadAIService.callBackendStream(endpoint, messages, wrappedOnChunk, wrappedOnComplete, wrappedOnError, reportContext, onStatus);
  },

  // Backend streaming implementation - optimized for proxy environments
  callBackendStream: async (endpoint, messages, onChunk, onComplete, onError, reportContext = null, onStatus = null) => {
    const config = RadAIService.getConfig();
    
    try {
      const requestBody = {
        messages: messages,
        sessionId: Date.now().toString()
      };

      if (reportContext) {
        requestBody.reportContext = reportContext;
      }

      // Enhanced headers for proxy compatibility
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0',
          // Proxy-specific headers
          'X-Accel-Buffering': 'no', // Nginx
          'X-Proxy-Buffering': 'no', // Generic
          'Connection': 'keep-alive'
        },
        credentials: 'include', // Include session cookies
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Backend API error: ${response.statusText}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let lastChunkTime = Date.now();
      let currentEventName = 'message'; // SSE event name of the next data line

      while (true) {
        const { done, value } = await reader.read();
        
        if (done) {
          onComplete();
          break;
        }

        const currentTime = Date.now();
        const chunkDelay = currentTime - lastChunkTime;
        lastChunkTime = currentTime;

        // Decode the chunk
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        // Process each line immediately - critical for proxy environments
        for (const line of lines) {
          if (line.trim() === '') continue;
          
          if (line.startsWith('event:')) {
            const eventType = line.slice(6).trim();
            if (eventType === 'done') {
              onComplete();
              return;
            }
            currentEventName = eventType;
            continue;
          }

          if (line.startsWith('data:')) {
            const data = line.slice(5); // Don't trim yet, preserve spaces

            // Progress status events ({"s":"Searching the web..."}) - shown
            // while waiting for the first token; never part of the answer.
            if (currentEventName === 'status') {
              currentEventName = 'message';
              try {
                const status = JSON.parse(data.trim());
                if (onStatus && typeof status.s === 'string') onStatus(status.s);
              } catch (e) { /* cosmetic only - ignore malformed status */ }
              continue;
            }
            currentEventName = 'message';

            // Handle completion signals
            if (data.trim() === '[DONE]') {
              onComplete();
              return;
            }

            // Handle empty data (common in SSE streams)
            if (data === '' || data === ' ') {
              continue; // Skip empty data chunks
            }
            
            const trimmedData = data.trim();
            let handledAsJson = false;
            if (trimmedData.startsWith('{') || trimmedData.startsWith('[')) {
              try {
                const parsedData = JSON.parse(data);
                handledAsJson = true;
                if (parsedData.error) {
                  onError(new Error(parsedData.error));
                  return;
                }
                if (typeof parsedData.v === 'string') {
                  onChunk(parsedData.v);
                } else if (parsedData.content) {
                  onChunk(parsedData.content);
                } else if (parsedData.text) {
                  onChunk(parsedData.text);
                }
              } catch (parseError) {
                handledAsJson = false; // malformed JSON-looking text; fall through to raw path
              }
            }
            if (!handledAsJson && data !== '') {
              // Raw text chunk (legacy backend without the {"v":...} envelope).
              const cleanedData = RadAIService.cleanTextChunk(data);

              // Detect if we're getting large chunks (proxy buffering)
              if (cleanedData.length > 30) {
                // Large chunk detected - likely proxy buffering
                // Split into smaller pieces for smoother display
                RadAIService.simulateStreaming(cleanedData, onChunk, 20); // Faster simulation
              } else {
                // Normal streaming - send immediately
                onChunk(cleanedData);
              }
            }
          }
        }
      }
    } catch (error) {
      onError(error);
    }
  },

  // Legacy method for backward compatibility - now uses backend
  callOpenAIStream: async (messages, onChunk, onComplete, onError) => {
    // Default to Claire stream for backward compatibility (backend only)
    return RadAIService.callClaireStream(messages, onChunk, onComplete, onError);
  }
}